import { DsAvatar, DsBadge, DsButton } from '@frontends/ds';
import { DsIcon } from '@frontends/ds-icons';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { type ClientSession } from '@/types/Auth.ts';
import { getBrowserIcon } from './sessionDevice';
import { SessionMeta } from './SessionMeta';
import { useSessionFormatters } from './useSessionFormatters';

interface SessionItemProps {
  onOpenDetails: () => void;
  session: ClientSession;
  isCurrent?: boolean;
  onEnd?: () => void;
}

export function SessionItem({ session, isCurrent = false, onOpenDetails, onEnd }: SessionItemProps) {
  const { t } = useTranslation();
  const { getTitle } = useSessionFormatters();
  const titleId = useId();
  const metaId = useId();

  return (
    <li className="relative flex items-center gap-3 px-4 py-3 transition-colors hover:bg-backgroundHover">
      <DsAvatar
        shape="square"
        icon={getBrowserIcon(session.user_agent)}
        variant={isCurrent ? 'brand' : 'neutral'}
        aria-hidden
      />

      <div className="flex min-w-0 flex-1 flex-col wrap-break-word">
        <button
          id={titleId}
          type="button"
          aria-haspopup="dialog"
          aria-describedby={metaId}
          onClick={onOpenDetails}
          className="cursor-pointer text-start text-base font-medium text-textPrimary outline-none after:absolute after:inset-0 focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-brandPrimary focus-visible:after:outline-solid"
        >
          {getTitle(session)}
        </button>

        <SessionMeta id={metaId} session={session} isCurrent={isCurrent} />
      </div>

      {isCurrent && (
        <DsBadge variant="success" size="sm" showDot className="shrink-0">
          {t('sessions_active_now')}
        </DsBadge>
      )}

      {onEnd && (
        <DsButton
          variant="secondary"
          size="sm"
          aria-describedby={`${titleId} ${metaId}`}
          onPress={onEnd}
          className="relative z-10 shrink-0 max-sm:hidden"
        >
          {t('sessions_end_session')}
        </DsButton>
      )}

      <DsIcon name="chevron-right" size="sm" className="shrink-0 text-textTertiary sm:hidden rtl:-scale-x-100" />
    </li>
  );
}
